import { Cubit } from "blac-next";
import type { PDFDocumentLoadingTask } from "pdfjs-dist";
import { TextLayer } from "pdfjs-dist/legacy/build/pdf.mjs";
import type {
  PDFDocumentProxy,
  RenderTask,
  TextContent
} from "pdfjs-dist/types/src/display/api";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { tracker } from "src/state/state";
import {
  TrackEvent,
  TrackType,
  type TrackingData
} from "src/state/Track/TrackCubit";
import type { TranslationKey } from "src/types/translationKey";

export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 3.0;
export const DEFAULT_ZOOM = 1.0;
const ZOOM_STEP = 0.25;
const DEFAULT_FILENAME = "document.pdf";
const PDF_MIME_TYPE = "application/pdf";
const PAGE_SLOT_SELECTOR = "[data-page-slot]";
const PAGE_CONTENT_CLASS = "page-content";
const PAGE_CONTENT_SELECTOR = `.${PAGE_CONTENT_CLASS}`;

export interface FileViewContext {
  fileId?: string;
  contentType?: string;
  contentLength?: number;
  source?: string;
  wasCached?: boolean;
}

export interface PageDimension {
  width: number;
  height: number;
}

export interface PDFViewerState {
  isOpen: boolean;
  blobUrl: string | null;
  filename: string | null;
  currentPage: number;
  numPages: number;
  loading: boolean;
  error: TranslationKey | null;
  zoom: number;
  pageDimensions: PageDimension[];
}

const INITIAL_STATE: PDFViewerState = {
  isOpen: false,
  blobUrl: null,
  filename: null,
  currentPage: 1,
  numPages: 0,
  loading: false,
  error: null,
  zoom: DEFAULT_ZOOM,
  pageDimensions: []
};

export default class PDFViewerBloc extends Cubit<PDFViewerState> {
  pdf: PDFDocumentProxy | null = null;
  loadingTask: PDFDocumentLoadingTask | null = null;
  containerElement: HTMLDivElement | null = null;
  isProgrammaticScroll = false;
  pendingScrollTop: number | null = null;
  pendingScrollLeft: number | null = null;
  fileContext: FileViewContext | null = null;
  private maxPageViewed = 1;

  private readonly activeRenderTasks = new Map<number, RenderTask>();
  private readonly renderedPages = new Set<number>();
  private readonly renderedZooms = new Map<number, number>();
  private readonly textContentCache = new Map<number, TextContent>();

  constructor() {
    super(INITIAL_STATE);
  }

  // ── Lifecycle ──

  public readonly open = (
    blobUrl: string,
    filename: string,
    context?: FileViewContext
  ): void => {
    this.fileContext = context ?? null;
    this.maxPageViewed = 1;
    this.emit({
      ...INITIAL_STATE,
      isOpen: true,
      blobUrl,
      filename,
      loading: true
    });
    tracker.track(TrackEvent.FileViewer, {
      type: TrackType.open,
      data: this.trackingData()
    });
  };

  public readonly close = (): void => {
    if (this.state.isOpen) {
      tracker.track(TrackEvent.FileViewer, {
        type: TrackType.close,
        data: this.trackingData({
          numPages: this.state.numPages,
          maxPageViewed: this.maxPageViewed
        })
      });
    }
    this.cancelAllRenders();
    this.destroyLoadingTask();
    this.revokeBlobUrl();
    this.pendingScrollTop = null;
    this.pendingScrollLeft = null;
    this.fileContext = null;
    this.maxPageViewed = 1;
    this.emit({ ...INITIAL_STATE });
  };

  setContainerEl = (el: HTMLDivElement | null): void => {
    if (el) {
      this.containerElement = el;
    }
  };

  // ── Document loading ──

  openDocument = async (loadingTask: PDFDocumentLoadingTask): Promise<void> => {
    this.loadingTask = loadingTask;
    try {
      const pdf = await loadingTask.promise;
      this.pdf = pdf;

      const dimensions = await this.extractPageDimensions(pdf);

      this.patch({
        numPages: pdf.numPages,
        currentPage: 1,
        loading: false,
        error: null,
        pageDimensions: dimensions
      });
    } catch (err: unknown) {
      if (!this.state.isOpen) {
        return;
      }
      if (this.isWorkerDestroyedError(err)) {
        return;
      }
      reportErrorSentry(err, this.trackingData({ failurePoint: "load" }));
      tracker.track(TrackEvent.FileViewer, {
        type: TrackType.fail,
        data: this.trackingData({
          failurePoint: "load",
          errorMessage: err instanceof Error ? err.message : String(err)
        })
      });
      this.patch({ error: "error.pdfLoadFailed", loading: false });
    }
  };

  // ── Page rendering ──

  renderPageToCanvas = async (
    pageNum: number,
    slotElement: HTMLDivElement,
    availableWidth: number
  ): Promise<void> => {
    if (!this.pdf) {
      return;
    }

    const targetZoom = this.state.zoom;
    if (this.isRenderedAtZoom(pageNum, targetZoom)) {
      return;
    }
    this.cancelRenderTask(pageNum);

    try {
      const page = await this.pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1 });
      const baseScale = availableWidth / viewport.width;
      const cssScale = baseScale * targetZoom;
      const pixelRatio = window.devicePixelRatio || 1;
      const renderViewport = page.getViewport({
        scale: cssScale * pixelRatio
      });

      const canvas = this.createCanvas(renderViewport, pixelRatio);
      const ctx = canvas.getContext("2d", { alpha: false });
      if (!ctx) {
        return;
      }

      const renderTask = page.render({
        canvasContext: ctx,
        viewport: renderViewport,
        canvas
      });

      this.activeRenderTasks.set(pageNum, renderTask);
      await renderTask.promise;
      this.activeRenderTasks.delete(pageNum);

      if (this.state.zoom !== targetZoom) {
        return;
      }

      this.swapCanvas(slotElement, canvas);
      this.renderedPages.add(pageNum);
      this.renderedZooms.set(pageNum, targetZoom);

      const wrapper = slotElement.querySelector<HTMLDivElement>(
        PAGE_CONTENT_SELECTOR
      );
      if (wrapper) {
        await this.renderTextLayerForPage(pageNum, wrapper, targetZoom);
      }
    } catch (err: unknown) {
      this.activeRenderTasks.delete(pageNum);
      if (this.isIgnorableRenderError(err)) {
        return;
      }
      reportErrorSentry(
        err,
        this.trackingData({
          failurePoint: "render",
          page: pageNum,
          zoom: targetZoom
        })
      );
      tracker.track(TrackEvent.FileViewer, {
        type: TrackType.fail,
        data: this.trackingData({
          failurePoint: "render",
          page: pageNum,
          zoom: targetZoom,
          errorMessage: err instanceof Error ? err.message : String(err)
        })
      });
      this.patch({ error: "error.pdfRenderFailed" });
    }
  };

  clearPage = (pageNum: number): void => {
    this.cancelRenderTask(pageNum);
    this.renderedPages.delete(pageNum);
    this.renderedZooms.delete(pageNum);
  };

  isPageRendered = (pageNum: number): boolean => {
    return this.isRenderedAtZoom(pageNum, this.state.zoom);
  };

  setCurrentPage = (page: number): void => {
    if (page !== this.state.currentPage) {
      this.maxPageViewed = Math.max(this.maxPageViewed, page);
      this.patch({ currentPage: page });
    }
  };

  // ── Zoom ──

  setZoom = (zoom: number): void => {
    const clamped = Math.max(MIN_ZOOM, Math.min(zoom, MAX_ZOOM));
    if (clamped === this.state.zoom) {
      return;
    }
    this.prepareZoomTransition(clamped);
    this.patch({ zoom: clamped });
  };

  zoomIn = (): void => {
    this.setZoom(this.state.zoom + ZOOM_STEP);
  };

  zoomOut = (): void => {
    this.setZoom(this.state.zoom - ZOOM_STEP);
  };

  resetZoom = (): void => {
    this.setZoom(DEFAULT_ZOOM);
  };

  // ── Share / Download ──

  download = (): void => {
    const { blobUrl, filename } = this.state;
    if (!blobUrl) {
      return;
    }

    tracker.track(TrackEvent.FileViewer, {
      type: TrackType.click,
      data: this.trackingData({ action: "download" })
    });

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename ?? DEFAULT_FILENAME;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  share = async (): Promise<void> => {
    const { blobUrl, filename } = this.state;
    if (!blobUrl) {
      return;
    }

    tracker.track(TrackEvent.FileViewer, {
      type: TrackType.click,
      data: this.trackingData({ action: "share" })
    });

    try {
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      const file = new File([blob], filename ?? DEFAULT_FILENAME, {
        type: PDF_MIME_TYPE
      });
      await navigator.share({ files: [file] });
    } catch (err: unknown) {
      if (err instanceof Error && err.name === "AbortError") {
        return;
      }
      reportErrorSentry(err, this.trackingData({ failurePoint: "share" }));
    }
  };

  // ── Private: tracking helpers ──

  private trackingData(extra?: TrackingData): TrackingData {
    const ctx = this.fileContext;
    return {
      fileId: ctx?.fileId,
      fileName: this.state.filename ?? undefined,
      fileType: ctx?.contentType ?? "application/pdf",
      fileSize: ctx?.contentLength,
      source: ctx?.source,
      wasCached: ctx?.wasCached,
      ...extra
    };
  }

  // ── Private: zoom helpers ──

  private prepareZoomTransition(targetZoom: number): void {
    if (!this.containerElement) {
      return;
    }
    const container = this.containerElement;
    const ratio = targetZoom / this.state.zoom;

    const anchorX = container.scrollLeft + container.clientWidth / 2;
    const anchorY = container.scrollTop + container.clientHeight / 2;
    this.pendingScrollLeft = Math.max(
      0,
      anchorX * ratio - container.clientWidth / 2
    );
    this.pendingScrollTop = Math.max(
      0,
      anchorY * ratio - container.clientHeight / 2
    );

    const slots = container.querySelectorAll<HTMLElement>(PAGE_SLOT_SELECTOR);
    for (const slot of slots) {
      const pageNum = parseInt(slot.dataset.pageSlot ?? "0", 10);
      const renderedZoom = this.renderedZooms.get(pageNum);
      const wrapper = slot.querySelector<HTMLDivElement>(PAGE_CONTENT_SELECTOR);
      if (wrapper && renderedZoom != null) {
        const cssScale = targetZoom / renderedZoom;
        wrapper.style.transform = cssScale === 1 ? "" : `scale(${cssScale})`;
      }
    }
  }

  // ── Private: rendering helpers ──

  private isRenderedAtZoom(pageNum: number, zoom: number): boolean {
    return (
      this.renderedPages.has(pageNum) &&
      this.renderedZooms.get(pageNum) === zoom
    );
  }

  private async extractPageDimensions(
    pdf: PDFDocumentProxy
  ): Promise<PageDimension[]> {
    const dimensions: PageDimension[] = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const vp = page.getViewport({ scale: 1 });
      dimensions.push({ width: vp.width, height: vp.height });
    }
    return dimensions;
  }

  private createCanvas(
    viewport: { width: number; height: number },
    pixelRatio: number
  ): HTMLCanvasElement {
    const canvas = document.createElement("canvas");
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    canvas.style.width = `${Math.floor(viewport.width / pixelRatio)}px`;
    canvas.style.height = `${Math.floor(viewport.height / pixelRatio)}px`;
    return canvas;
  }

  private swapCanvas(
    slotElement: HTMLDivElement,
    newCanvas: HTMLCanvasElement
  ): void {
    let wrapper = slotElement.querySelector<HTMLDivElement>(
      PAGE_CONTENT_SELECTOR
    );
    if (!wrapper) {
      wrapper = document.createElement("div");
      wrapper.className = PAGE_CONTENT_CLASS;
      slotElement.appendChild(wrapper);
    }
    wrapper.querySelector(".textLayer")?.remove();
    const oldCanvas = wrapper.querySelector("canvas");
    wrapper.appendChild(newCanvas);
    oldCanvas?.remove();
    wrapper.style.transform = "";
  }

  private cancelRenderTask(pageNum: number): void {
    const task = this.activeRenderTasks.get(pageNum);
    if (!task) {
      return;
    }
    try {
      task.cancel();
    } catch {
      // pdf.js may throw if the task is already settled
    }
    this.activeRenderTasks.delete(pageNum);
  }

  private cancelAllRenders(): void {
    for (const [, task] of this.activeRenderTasks) {
      try {
        task.cancel();
      } catch {
        // pdf.js may throw if the task is already settled
      }
    }
    this.activeRenderTasks.clear();
    this.renderedPages.clear();
    this.renderedZooms.clear();
    this.textContentCache.clear();
  }

  // ── Text layer ──

  private async renderTextLayerForPage(
    pageNum: number,
    wrapper: HTMLDivElement,
    targetZoom: number
  ): Promise<void> {
    if (!this.pdf) {
      return;
    }

    try {
      const page = await this.pdf.getPage(pageNum);
      const canvas = wrapper.querySelector("canvas");
      if (!canvas || this.state.zoom !== targetZoom) {
        return;
      }

      const cssWidth = parseFloat(canvas.style.width);
      const baseViewport = page.getViewport({ scale: 1 });
      const cssScale = cssWidth / baseViewport.width;
      const cssViewport = page.getViewport({ scale: cssScale });

      let textContent = this.textContentCache.get(pageNum);
      if (!textContent) {
        textContent = await page.getTextContent();
        this.textContentCache.set(pageNum, textContent);
      }
      if (this.state.zoom !== targetZoom || !this.renderedPages.has(pageNum)) {
        return;
      }

      wrapper.querySelector(".textLayer")?.remove();
      const textLayerDiv = document.createElement("div");
      textLayerDiv.className = "textLayer";
      textLayerDiv.style.setProperty("--total-scale-factor", String(cssScale));
      textLayerDiv.style.setProperty("--scale-round-x", "1px");
      textLayerDiv.style.setProperty("--scale-round-y", "1px");
      wrapper.appendChild(textLayerDiv);

      const textLayer = new TextLayer({
        textContentSource: textContent,
        container: textLayerDiv,
        viewport: cssViewport
      });
      await textLayer.render();

      if (this.state.zoom !== targetZoom || !this.renderedPages.has(pageNum)) {
        textLayerDiv.remove();
      }
    } catch {
      // text layer is non-critical
    }
  }

  // ── Private: lifecycle helpers ──

  private revokeBlobUrl(): void {
    if (!this.state.blobUrl) {
      return;
    }
    try {
      URL.revokeObjectURL(this.state.blobUrl);
    } catch {
      // Ignore revocation errors
    }
  }

  private destroyLoadingTask(): void {
    if (!this.loadingTask) {
      return;
    }
    void this.loadingTask.destroy();
    this.loadingTask = null;
    this.pdf = null;
  }

  // ── Private: error classification ──

  private isIgnorableRenderError(err: unknown): boolean {
    return (
      this.isRenderCancelledError(err) ||
      this.isWorkerDestroyedError(err) ||
      !this.state.isOpen
    );
  }

  private isWorkerDestroyedError(err: unknown): boolean {
    return err instanceof Error && err.message.includes("Worker was destroyed");
  }

  private isRenderCancelledError(err: unknown): boolean {
    return err instanceof Error && err.name === "RenderingCancelledException";
  }
}
